import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import { Avatar, Mascot, ThemeToggle, spring } from '@redpandev/ui';
import type { PublicUserDto } from '@redpandev/shared';
import { useAuth } from '../auth/AuthContext';
import { avatarSources } from '../lib/avatars';
import { useTheme } from '../app/theme/ThemeProvider';
import { useInstallPrompt } from './pwa/InstallPromptProvider';

/** Lien « pill » de la navbar : mêmes tokens que Button, sémantique <a>. */
const NAV_PRIMARY =
  'inline-flex h-11 items-center justify-center rounded-full bg-panda-500 px-4 font-display text-sm ' +
  'font-semibold text-ink-950 transition-[box-shadow,filter] duration-fast ease-fun ' +
  'hover:shadow-glow-panda hover:brightness-110 sm:px-5 sm:text-base';

const MENU_ITEM =
  'flex min-h-11 w-full items-center rounded-md px-3 py-2 text-left text-sm font-medium ' +
  'text-ink-100 transition-colors duration-fast ease-fun hover:bg-ink-100/10';

/**
 * Dans un jeu, la marque du header affiche le NOM DU JEU (même lien → hub) : un seul
 * header partout (pas d'entête spécifique par vue), le contexte reste évident.
 */
const GAME_TITLES: ReadonlyArray<readonly [prefix: string, title: string]> = [
  ['/griffonne', 'Griffonne'],
];

function brandTitleFor(pathname: string): string {
  return GAME_TITLES.find(([p]) => pathname === p || pathname.startsWith(`${p}/`))?.[1] ?? 'RedPan';
}

/** Avatar + popover animé (Mon compte / Déconnexion). Cibles ≥ 44px. */
function AccountMenu({ user, onLogout }: { user: PublicUserDto; onLogout: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const install = useInstallPrompt();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fermeture au clic/tap en dehors.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: Event) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Focus le premier item à l'ouverture (navigation clavier).
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  const close = (refocusTrigger = false) => {
    setOpen(false);
    if (refocusTrigger) triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'ArrowDown'
        ? (current + 1) % items.length
        : (current - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Menu du compte — ${user.displayName}`}
        className={
          'flex h-11 items-center gap-2 rounded-full border border-ink-600/60 bg-ink-700/60 py-1 pl-1 pr-2 ' +
          'transition-[border-color,box-shadow] duration-fast ease-fun hover:border-panda-500/60 hover:shadow-glow-panda sm:pr-3'
        }
      >
        <Avatar {...avatarSources(user.avatarId)} size={36} />
        <span className="hidden max-w-[10rem] truncate font-display text-sm font-medium text-ink-100 sm:block">
          {user.displayName}
        </span>
        <svg
          viewBox="0 0 12 12"
          width="10"
          height="10"
          aria-hidden="true"
          className={`text-ink-400 transition-transform duration-fast ease-fun ${open ? 'rotate-180' : ''}`}
        >
          <path
            d="M2 4 L6 8 L10 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={menuRef}
            role="menu"
            aria-label="Menu du compte"
            onKeyDown={handleMenuKeyDown}
            initial={{ opacity: 0, scale: 0.92, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={spring.soft}
            className={
              'absolute right-0 top-full z-dropdown mt-2 w-56 origin-top-right rounded-lg ' +
              'border border-ink-600/60 bg-surface p-1.5 shadow-pop'
            }
          >
            <Link role="menuitem" to="/profile" onClick={() => close()} className={MENU_ITEM}>
              Mon profil
            </Link>
            <Link role="menuitem" to="/settings" onClick={() => close()} className={MENU_ITEM}>
              Paramètres
            </Link>
            {install.available && (
              <button
                role="menuitem"
                type="button"
                className={MENU_ITEM}
                onClick={() => {
                  close();
                  install.open();
                }}
              >
                Installer l'app 🐾
              </button>
            )}
            <button
              role="menuitem"
              type="button"
              className={MENU_ITEM}
              onClick={() => {
                close();
                void onLogout();
              }}
            >
              Déconnexion
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Header() {
  const { status, user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const brand = brandTitleFor(pathname);
  // Au survol/focus du logo, Redpan s'excite (clignement déjà géré par Mascot).
  const [logoLively, setLogoLively] = useState(false);

  return (
    <header className="sticky top-0 z-sticky border-b border-ink-600/40 bg-app/70 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-content items-center justify-between gap-2 px-3 sm:px-6">
        <Link
          to="/"
          aria-label={`${brand} — retour au hub`}
          className="flex h-11 items-center gap-2 rounded-full pr-2"
          onMouseEnter={() => setLogoLively(true)}
          onMouseLeave={() => setLogoLively(false)}
          onFocus={() => setLogoLively(true)}
          onBlur={() => setLogoLively(false)}
        >
          <Mascot mood={logoLively ? 'excited' : 'happy'} size={32} />
          <span className="bg-gradient-to-r from-panda-500 to-panda-600 bg-clip-text font-display text-xl font-bold text-transparent">
            {brand}
          </span>
        </Link>

        <nav aria-label="Navigation principale" className="flex items-center gap-1.5 sm:gap-3">
          <ThemeToggle theme={theme} onToggle={toggle} />
          {status === 'authenticated' && user ? (
            <AccountMenu
              user={user}
              onLogout={async () => {
                await logout();
                navigate('/');
              }}
            />
          ) : status === 'anonymous' ? (
            // Un SEUL CTA (« Connexion ») : deux liens débordent sur les petits écrans
            // mobiles ; l'inscription reste à un clic (« Pas de compte ? » sur /login).
            <Link to="/login" className={NAV_PRIMARY}>
              Connexion
            </Link>
          ) : null}
        </nav>
      </div>
    </header>
  );
}
